import React, { useState } from "react";
import { Form } from "react-final-form";
import { useSelector } from "react-redux";
import { Alert, Box, Button } from "@mui/material";
import { useSnackbar } from "notistack";

import { LoadingButton } from "ui";

import analytics from "fond/analytics";
import { selectFoldersForReportMove, useUpdateReportMutation } from "fond/api";
import { HOME_FOLDER_ID } from "fond/constants";
import { FolderPickerField } from "fond/form/fields/FolderPickerField";
import { Report, Store } from "fond/types";
import { Actions, permissionCheck } from "fond/utils/permissions";
import { Modal } from "fond/widgets";

interface IFormData {
  FolderId: string;
}

interface IProps {
  report: Report;
  onClose(): void;
}

const MoveReportDialog: React.FC<IProps> = ({ report, onClose }: IProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const [saving, setSaving] = useState(false);
  const folders = useSelector((state: Store) => selectFoldersForReportMove(state, report));
  const canMove = report?.Permission?.Level != null ? permissionCheck(report?.Permission?.Level, Actions.REPORT_MOVE) : false;
  const [updateReport] = useUpdateReportMutation();

  /**
   * On submit function called when the form is submitted and valid
   */
  const onSubmit = async (values: IFormData) => {
    const folderId = values.FolderId;
    setSaving(true);

    try {
      await updateReport({ ID: report.ID, FolderID: folderId === HOME_FOLDER_ID ? null : folderId }).unwrap();
      enqueueSnackbar("The Report has been moved");
      analytics.track("Report.Move.Moved report", { reportId: report.ID });
    } catch {
      enqueueSnackbar("Report move failed. Please try again...", { variant: "error" });
    } finally {
      setSaving(false);
      onClose();
    }
  };

  const modalContent = !canMove ? (
    <Box mb={2}>
      <Alert severity="info">You do not have permission to move this report.</Alert>
    </Box>
  ) : (
    <>
      <Box mb={2} data-testid="move-alert-message">
        <Alert severity="warning">Moving a report to a new location has the potential to change who can access & edit this report.</Alert>
      </Box>
      <Form<IFormData>
        onSubmit={onSubmit}
        initialValues={{ FolderId: report.Folder?.ID || HOME_FOLDER_ID }}
        render={({ handleSubmit }) => {
          return (
            <form onSubmit={handleSubmit} id="move-entity-form">
              <FolderPickerField name="FolderId" folders={folders} account={report.Account} />
            </form>
          );
        }}
      />
    </>
  );

  return (
    <Modal
      open
      header="Move this report"
      data-testid="move-report-modal"
      content={modalContent}
      actions={
        <>
          <Button data-testid="move-folder-cancel-button" color="primary" onClick={onClose} sx={{ marginRight: 1 }}>
            Cancel
          </Button>
          {canMove && (
            <LoadingButton form="move-entity-form" type="submit" data-testid="move-folder-save-button" color="primary" loading={saving}>
              Update
            </LoadingButton>
          )}
        </>
      }
    />
  );
};

export default MoveReportDialog;
